// "Procesar el día" — B-60, what replaces the macro's "EJECUTAR PROCESO BANCAMIA" button. It lives
// inside "Nuevo cierre" (`NuevoCierreDialog.jsx`): for a client whose reports cover all its offices
// at once, the dialog shows this instead of the office picker.
//
// The client-level twin of the manual pipeline: `CierreManualDialog` works one office's closing;
// this sends the client's daily files once (`runCierreDailyProcess`, B-52) and every office of the
// day reads them. This file draws only the files: the date and the "Procesar el día" button belong
// to the dialog's own form, and the day's progress has its own dialog (`AvanceDelDiaDialog.jsx`),
// so the form stays short. The state is `useConciliacionDia`.
//
// Nothing here names a client. The slots are the keys `getCierreDailyProgress` counts — the
// client's `scope: client` declarations — and their labels come from the client's names file.
//
// It does not reuse `SlotDocumento`: that one uploads and reads per closing, and this send has no
// closing yet. Nor `FilesDragAndDrop`, which has no click-to-browse and is styled outside the
// Brinks theme.
//
// The three reports can also be dropped together on "Subir todos los reportes" (`ZonaArchivos`):
// each lands on its row by its file name (`asignarArchivos`), and what it cannot place is said
// and left for its row.
//
// BR-38 — `permitido` false (no `cierres.daily.run` on the whole client) keeps the date and the
// progress, which are reads, and disables the files with `motivo` said under them; the dialog
// disables the send.
import { useEffect, useState } from "react";
import { ChevronRight, FileCheck2, FileSpreadsheet, Info, Loader2 } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, cn } from "@mantis-sas/ui";

import { acceptDeFormato } from "../../../utils/cierreManual";
import { fechaDMY } from "../../../utils/cierrePanel";
import { ayudaDeFuente, nombreDeFuente } from "../../../utils/cierreNombres";
import { NombreDeArchivo } from "./NombreDeArchivo";
import { asignarArchivos, avisoDeAsignacion } from "../../../utils/cierreCargaAgrupada";
import { expandirZips } from "../../../utils/cierreZip";
import { clavesSoloCliente, formatoDeClave, formatosDelDia } from "../../../utils/cierreDiaria";
import { ZonaArchivos } from "./ZonaArchivos";
import { guia } from "../../../utils/cierreTours";

export const TONO = {
  ok: "border-brinks-success-border bg-brinks-success-soft text-brinks-success",
  aviso: "border-brinks-warning-border bg-brinks-warning-soft text-brinks-warning",
  error: "border-brinks-danger-border bg-brinks-danger-soft text-brinks-danger",
  neutral: "border-brinks-border bg-brinks-surface text-brinks-muted-foreground",
};

export function Mensaje({ tono = "error", lineas }) {
  const texto = (Array.isArray(lineas) ? lineas : [lineas]).filter(Boolean);
  if (!texto.length) return null;
  return (
    <div role={tono === "error" ? "alert" : "status"} className={cn("space-y-0.5 rounded-[8px] border px-2.5 py-1.5 text-[12.5px]", TONO[tono])}>
      {texto.map((linea) => (
        <p key={linea}>{linea}</p>
      ))}
    </div>
  );
}

// One daily file, as one row. The format is always xls today, but it is the declaration's word, not
// ours — the backend checks the bytes either way.
//
// The browser's own file input ("Choose File · No file chosen") read as plain text, so nobody knew
// where to click. The input is kept — it is what opens the file dialog and what a screen reader
// announces — but visually hidden, and the whole row is its `<label>`: it also takes a dragged
// file. The source's help text sits behind an (i) so three reports fit without scrolling.
function CasillaArchivo({ clave, nombres, obligatoria, soloCliente, formato, archivo, deshabilitada, onElegir }) {
  const nombre = nombreDeFuente(nombres, clave);
  const ayuda = ayudaDeFuente(nombres, clave);
  const id = `archivo-${clave}-input`;
  const [encima, setEncima] = useState(false);

  const soltar = (e) => {
    e.preventDefault();
    setEncima(false);
    if (deshabilitada) return;
    const file = e.dataTransfer?.files?.[0];
    if (file) onElegir(clave, file);
  };
  return (
    <li
      onDragOver={(e) => {
        e.preventDefault();
        if (!deshabilitada) setEncima(true);
      }}
      onDragLeave={() => setEncima(false)}
      onDrop={soltar}
      className={cn("flex items-center gap-2.5 px-3 py-2 transition-colors", archivo && "bg-brinks-success-soft/60", encima && "bg-brinks-surface")}
    >
      <span className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-[8px]", archivo ? "bg-brinks-success-soft text-brinks-success" : "bg-brinks-surface text-brinks-muted-foreground")}>
        {archivo ? <FileCheck2 className="h-4 w-4" /> : <FileSpreadsheet className="h-4 w-4" />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1">
          <label htmlFor={id} className="min-w-0 break-words text-[13px] sm:truncate font-medium text-brinks-foreground">
            <NombreDeArchivo nombre={nombre} />
          </label>
          {ayuda ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="shrink-0 text-brinks-muted-foreground hover:text-brinks-foreground" aria-label={`Qué es ${nombre}`}>
                  <Info className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="brinks-scope font-redhat max-w-[260px] text-[12px]">{ayuda}</TooltipContent>
            </Tooltip>
          ) : null}
        </div>
        <p className="truncate text-[11.5px] text-brinks-muted-foreground">
          {archivo ? archivo.name : estadoDeCasilla({ obligatoria, soloCliente, formato })}
        </p>
      </div>
      <label
        htmlFor={id}
        className={cn(
          "inline-flex h-8 shrink-0 items-center rounded-[7px] border bg-card px-3 text-[12px] font-medium text-brinks-foreground transition",
          deshabilitada ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:border-brinks-border-strong"
        )}
      >
        {archivo ? "Cambiar" : "Elegir"}
      </label>
      <input
        id={id}
        type="file"
        accept={acceptDeFormato(formato)}
        disabled={deshabilitada}
        onChange={(e) => onElegir(clave, e.target.files?.[0] || null)}
        className="sr-only"
      />
    </li>
  );
}

// What a slot with no file says: mandatory, optional because no office reads it (B-101: it only
// feeds the client's day reconciliation), or optional because it was already sent this day.
const estadoDeCasilla = ({ obligatoria, soloCliente, formato }) => {
  if (obligatoria) return `Obligatorio · .${formato}`;
  if (soloCliente) return `Opcional · .${formato} · para el cuadre del día`;
  return "Opcional · ya enviado este día";
};

// What the day already has, in one line, with the way into its own dialog.
function EstadoDelDia({ dia, onVerAvance }) {
  const { avance, corriendo } = dia;
  // B-77 — the lock is per client: a run on another day also says `running` here, with this
  // day's counters, so it is named by its own date and without a percentage that is not its own.
  const otroDia = corriendo && dia.progreso?.runningDate && dia.progreso.runningDate !== dia.fecha ? dia.progreso.runningDate : "";
  if (!corriendo && !avance.total) return null;
  const problemas = avance.conProblema.length;
  // A run that stopped with offices never read (a failed send) is not a processed day.
  const incompleto = !corriendo && avance.pendientes > 0;
  return (
    <button
      type="button"
      onClick={onVerAvance}
      className={cn(
        "flex w-full items-center gap-2 rounded-[8px] border px-2.5 py-2 text-left text-[12.5px] transition hover:border-brinks-border-strong",
        TONO[corriendo ? "neutral" : problemas || incompleto ? "aviso" : "ok"]
      )}
    >
      {corriendo ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <FileCheck2 className="h-4 w-4 shrink-0" />}
      <span className="min-w-0 flex-1">
        {otroDia
          ? `Se está procesando el ${fechaDMY(otroDia)}`
          : corriendo
          ? `Hay un proceso corriendo para este día · ${avance.porcentaje}%`
          : incompleto
            ? `Proceso incompleto: ${avance.leidas} de ${avance.total} oficinas leídas`
            : `Este día ya se procesó: ${avance.total} oficinas${problemas ? ` · ${problemas} con problema` : ""}`}
      </span>
      <span className="flex shrink-0 items-center font-medium">
        {corriendo ? "Ver avance" : "Ver resultado"} <ChevronRight className="h-3.5 w-3.5" />
      </span>
    </button>
  );
}

// `dia` is `useConciliacionDia`'s value; the dialog owns it because it is also what tells the
// dialog this client has a daily process at all.
export function ProcesoDelDia({ dia, nombres, onVerAvance, permitido = true, motivo = "" }) {
  const listos = dia.claves.filter((clave) => dia.archivos[clave]).length;
  const deshabilitada = !permitido || dia.enviando || dia.corriendo;
  const [aviso, setAviso] = useState([]);
  // A send empties the rows (`ronda`); what was said about the last drop goes with them.
  useEffect(() => setAviso([]), [dia.ronda]);

  const [leyendo, setLeyendo] = useState(false);

  // The client's reports to their rows by name (the one that names the date wins between two
  // Drives). B-101 — an office's own documents are not sent here: each reaches its closing by the
  // office's email, so a folder's other files are only said, not kept.
  const repartir = async (soltados) => {
    setLeyendo(true);
    const { archivos, errores } = await expandirZips(soltados);
    const asignacion = asignarArchivos(archivos, dia.claves, nombres, {
      fecha: dia.fecha, reglas: dia.progreso?.clientFileRules,
    });
    Object.entries(asignacion.asignados).forEach(([clave, archivo]) => dia.elegirArchivo(clave, archivo));
    setLeyendo(false);
    const deOficinas = dia.progreso?.officeFileRules?.length ? asignacion.sinReconocer : [];
    setAviso([
      ...errores,
      ...avisoDeAsignacion(deOficinas.length ? { ...asignacion, sinReconocer: [] } : asignacion),
      ...(deOficinas.length
        ? [`No se suben aquí: ${deOficinas.map((f) => f.name).join(", ")}. Los documentos de cada oficina llegan por su correo.`]
        : []),
    ]);
  };

  // Every format a dropped folder may carry: the client's reports.
  const formatosZona = formatosDelDia(dia.progreso, dia.claves);

  return (
    <div className="space-y-2.5 pt-1">
      <EstadoDelDia dia={dia} onVerAvance={onVerAvance} />

      <section className="space-y-1.5">
        <div className="flex items-center justify-between">
          <h3 className="text-[12.5px] font-semibold text-brinks-foreground">Reportes del día</h3>
          <span className="text-[11.5px] tabular-nums text-brinks-muted-foreground">
            {listos} de {dia.claves.length} listos
          </span>
        </div>
        <ZonaArchivos
          id="reportes-del-dia-varios"
          titulo="Subir todos los reportes"
          ayuda="Elige o arrastra los reportes juntos, o su carpeta o zip; cada uno va a su fila por el nombre del archivo."
          accept={[...formatosZona.map(acceptDeFormato), ".zip"].join(",")}
          deshabilitada={deshabilitada || leyendo}
          onArchivos={repartir}
        />
        {leyendo ? <Mensaje tono="neutral" lineas={["Leyendo los archivos…"]} /> : null}
        <Mensaje tono="aviso" lineas={aviso} />
        <TooltipProvider delayDuration={150}>
          {/* Remounted after each send, so the inputs read empty again. */}
          <ul {...guia("Reportes del día", "Un renglón por reporte que comparten todas las oficinas. Arrastra el archivo sobre su renglón o toca \"Elegir\". La (i) dice qué es cada reporte.")} key={dia.ronda} className="divide-y overflow-hidden rounded-[10px] border bg-card">
            {dia.claves.map((clave) => (
              <CasillaArchivo
                key={clave}
                clave={clave}
                nombres={nombres}
                obligatoria={dia.obligatorias.includes(clave)}
                soloCliente={clavesSoloCliente(dia.progreso).includes(clave)}
                formato={formatoDeClave(dia.progreso, clave)}
                archivo={dia.archivos[clave]}
                deshabilitada={deshabilitada}
                onElegir={dia.elegirArchivo}
              />
            ))}
          </ul>
        </TooltipProvider>
        <p className="text-[11.5px] text-brinks-muted-foreground">
          {dia.corriendo
            ? "Espera a que termine el proceso en curso para enviar otro archivo."
            : dia.obligatorias.length
              ? "La primera vez se necesitan todos los reportes. Puedes arrastrarlos sobre cada fila."
              : "Este día ya se procesó: al procesarlo de nuevo se rehacen todas las oficinas; los reportes que no envíes se toman de la carga anterior."}
        </p>
        {dia.progreso?.officeFileRules?.length ? (
          <p className="text-[11.5px] text-brinks-muted-foreground">
            Aquí van solo los reportes con todas las oficinas. Los documentos de cada oficina llegan por su correo.
          </p>
        ) : null}
      </section>

      {permitido ? null : <Mensaje tono="neutral" lineas={motivo} />}
      <Mensaje lineas={dia.errorEnvio} />
      {/* The upload itself takes seconds for three Bantotal reports; the button alone is too quiet. */}
      {dia.enviando ? (
        <Mensaje tono="neutral" lineas="Subiendo los reportes al servidor. Puede tardar hasta un minuto; no cierres esta ventana." />
      ) : null}
    </div>
  );
}
